import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CardListCell } from 'src/app/components/pages-partials/card-list/card-list-cell';
import { GradientProps } from 'src/app/interfaces/gradient-props';
import {
  BaseCard,
  DynamicDashboardContent,
  GraphCard,
  ListCard,
} from 'src/app/services/application-resources/models/page-resource-content';
import {  ChartOptions, ChartType } from 'chart.js';
import { DynamicTableCellsParserService } from '../dynamic-list-2/services/dynamic-table-cells-parser.service';

interface CardWithProps {
  card: BaseCard;
  cellProps: CardListCell[];
  cardParams: {
    title: string;
    category: string;
  };
  chartType: ChartType;
  chartGradientProps: GradientProps;
  chartOptions: ChartOptions;
}

@Component({
    selector: 'app-dynamic-dashboard',
    templateUrl: './dynamic-dashboard.component.html',
    styleUrls: ['./dynamic-dashboard.component.scss'],
    standalone: false
})
export class DynamicDashboardComponent implements OnInit {
  public mainChartSettings: ChartOptions;
  public mainChartGradientProps: GradientProps;

  dynamicDashboardContent: DynamicDashboardContent;
  cards: CardWithProps[];
  mainChartDataUrl: string;

  constructor(
    private route: ActivatedRoute,
    private dynamicTableCellsParserService: DynamicTableCellsParserService
  ) {
    const mainChartColor = '#FFFFFF';
    this.mainChartSettings = {
      layout: {
        padding: {
          left: 20,
          right: 20,
          top: 0,
          bottom: 0,
        },
      },
      elements: {
        point: {
          radius: 5,
          hoverRadius: 7,
          borderWidth: 2,
          borderColor: mainChartColor,
          backgroundColor: '#2c2c2c',
        },
        line: {
          borderWidth: 2,
          borderColor: mainChartColor,
        },
      },
      maintainAspectRatio: false,
      // tooltips: {
      //   backgroundColor: '#fff',
      //   titleFontColor: '#333',
      //   bodyFontColor: '#666',
      //   bodySpacing: 4,
      //   xPadding: 12,
      //   mode: 'nearest',
      //   intersect: false,
      //   position: 'nearest',
      // },
      // legend: {
      //   position: 'bottom',
      //   display: false,
      // },
      scales: {
        y: 
          {
            ticks: {
              // fontColor: 'rgba(255,255,255,0.4)',
              // fontStyle: 'bold',
              // beginAtZero: true,
              maxTicksLimit: 5,
              padding: 10,
            },
            grid: {
              drawTicks: true,
              // drawBorder: false,
              display: true,
              color: 'rgba(255,255,255,0.1)',
              // zeroLineColor: 'transparent',
            },
          },
    
        x: 
          {
            grid: {
              // zeroLineColor: 'transparent',
              display: false,
            },
            ticks: {
              padding: 10,
              // fontColor: 'rgba(255,255,255,0.4)',
              // fontStyle: 'bold',
            },
          },
        
      },
    };
  }

  ngOnInit(): void {
    this.route.data.subscribe((routeData) => {
      this.dynamicDashboardContent = routeData as DynamicDashboardContent;
      this.mainChartDataUrl = this.dynamicDashboardContent.mainChartDataUrl;

      this.cards = this.parseCards(this.dynamicDashboardContent.cards);
    });

    let routeParams: { [x: string]: any };
    this.route.params.subscribe((params) => (routeParams = params));
  }

  parseCards(cards: BaseCard[]): Array<CardWithProps> {
    let cardsWithProps: Array<CardWithProps> = cards.map((card) => {
      let customProps;
      if (card.type === 'LIST') {
        customProps = {
          cellProps: this.getCellPropsFromCard(card as ListCard),
        };
      } else {
        customProps = {
          chartType: this.getChartType(card as GraphCard),
          chartGradientProps: this.getChartGradientProps(card as GraphCard),
          chartOptions: this.getChartOptionsForGraphCard(card as GraphCard),
        };
      }

      return {
        card,
        cardParams: this.getCardParams(card),
        ...customProps,
      };
    });

    return cardsWithProps;
  }

  getCardParams(card: BaseCard): { title: string; category: string } {
    const cardParams = {
      title: card.title,
      category: card.category,
    };

    return cardParams;
  }

  getCellPropsFromCard(card: ListCard): CardListCell[] {
    return this.dynamicTableCellsParserService.parse(card.cols);
  }

  getChartType(card: GraphCard) {
    return card.chartType;
  }

  getChartGradientProps(card: GraphCard): GradientProps {
    return {
      points: [0, 170, 0, 50],
      settings: [
        {
          offset: 0,
          color: 'rgba(128, 182, 244, 0)',
        },
        {
          offset: 1,
          color: 'rgba(249, 99, 59, 0.40)',
        },
      ],
    };
  }

  getChartOptionsForGraphCard(card: GraphCard): ChartOptions {
    let chartElements: any;
    let yAxes;
    if (card.chartType === 'line') {
      chartElements = {
        point: {
          radius: 5,
          hoverRadius: 7,
          borderWidth: 2,
          borderColor: '#fff',
          backgroundColor: card.color,
        },
        line: {
          borderWidth: 2,
          borderColor: card.color,
        },
      };
      yAxes = [
        {
          display: false,
          ticks: {
            display: false,
          },
          gridLines: {
            zeroLineColor: 'transparent',
            drawTicks: false,
            display: false,
            drawBorder: false,
          },
        },
      ];
    } else {
      chartElements = {
        rectangle: {
          borderWidth: 2,
          borderColor: card.color,
        },
      };
      yAxes = [
        {
          gridLines: {
            zeroLineColor: 'transparent',
            drawBorder: false,
          },
        },
      ];
    }

    return {
      maintainAspectRatio: false,
      // legend: {
      //   display: false,
      // },
      elements: chartElements,
      // tooltips: {
      //   bodySpacing: 4,
      //   mode: 'nearest',
      //   intersect: false,
      //   position: 'nearest',
      //   xPadding: 10,
      //   yPadding: 10,
      //   caretPadding: 10,
      // },
      responsive: true,
      scales: {
        y: yAxes,
        x: 
          {
            display: false,
            ticks: {
              display: false,
            },
            grid: {
              // zeroLineColor: 'transparent',
              drawTicks: false,
              display: false,
              // drawBorder: false,
            },
          },        
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 15,
          bottom: 15,
        },
      },
    };
  }
}
